'use client';

import a__default, { forwardRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { classNames } from "../../utils/styles/classNames.js";
import { FormControl } from "../../atoms/FormControl/FormControl.js";
import { CaretUp, CaretDown } from "../../../react-icons.js";
import { Text } from "../../atoms/Text/Text.js";
import { useId } from "../../utils/hooks/useId.js";
import { Option } from "./Select.Option.js";
import { wrapper, focused, inputBorder, input, inputLabel, listDisplay, listHeight, list } from "./Select.css.js";
import { Loading } from "./Select.Loading.js";
import { Empty } from "./Select.Empty.js";
import { parseProps } from "./utils/parseProps.js";
import { getCurrentState } from "./utils/getCurrentState.js";
import { useFloatingUi } from "./utils/useFloatingUi.js";
import { getLabelId } from "./utils/constants.js";
function SelectComponent(props, ref) {
  var _a;
  const { selectFieldProps, formControlProps } = parseProps(props);
  const {
    options,
    dataQa,
    onChange,
    emptyLabel,
    isLoading,
    placeholder,
    value,
    noOptionsProvided
  } = selectFieldProps;
  const { disabled, error, label, ...restFormControlProps } = formControlProps;
  const autoId = useId();
  const labelId = getLabelId(autoId);
  const currentState = getCurrentState({
    disabled,
    error
  });
  const optionsContent = useMemo(() => options == null ? void 0 : options.map((i) => i.label), [options]);
  const selectedIndex = (options == null ? void 0 : options.findIndex((i) => i.value === value)) ?? -1;
  const [isOpen, setIsOpen] = a__default.useState(false);
  const handleOpenChange = useCallback(
    (o) => {
      if (!disabled) {
        setIsOpen(o);
      }
    },
    [setIsOpen, disabled]
  );
  const handleChange = useCallback(
    (i) => {
      if (options) {
        onChange(options[i].value);
      }
    },
    [onChange, options]
  );
  const {
    dropdownProps,
    labelProps,
    getItemProps,
    triggerProps,
    activeIndex,
    listRef,
    isTypingRef,
    context
  } = useFloatingUi({
    isOpen,
    onOpenChange: handleOpenChange,
    onSelect: handleChange,
    optionsContent,
    selectedIndex,
    triggerRef: ref
  });
  const handleSelect = (index) => {
    handleChange(index);
    setIsOpen(false);
    if (context.refs.domReference) {
      const domReference = context.refs.domReference;
      domReference.current.focus();
    }
  };
  const selectedOptionLabel = selectedIndex !== -1 && !!options ? (_a = options[selectedIndex]) == null ? void 0 : _a.label : void 0;
  const hasSelectedOption = !!selectedOptionLabel;
  const triggerTextColor = hasSelectedOption ? "onLight.text.primary" : "onLight.text.tertiary";
  const CaretIcon = isOpen ? CaretUp : CaretDown;
  const hasCaption = !!(options == null ? void 0 : options.find((i) => !!i.caption));
  return /* @__PURE__ */ a__default.createElement(
    FormControl,
    {
      ...restFormControlProps,
      disabled,
      error,
      label: { ...label, ...labelProps, id: labelId },
      a11yAnnounceRequired: true
    },
    /* @__PURE__ */ a__default.createElement("div", { className: wrapper }, /* @__PURE__ */ a__default.createElement(
      "div",
      {
        ...triggerProps,
        "aria-autocomplete": "none",
        "aria-labelledby": labelId,
        className: classNames(
          input,
          inputBorder[currentState],
          isOpen && focused
        ),
        "data-qa": dataQa
      },
      /* @__PURE__ */ a__default.createElement(
        Text,
        {
          "aria-hidden": !hasSelectedOption && placeholder ? "true" : void 0,
          className: inputLabel,
          color: disabled ? "onLight.disabled.muted" : triggerTextColor,
          variant: "body.base"
        },
        selectedOptionLabel || placeholder
      ),
      /* @__PURE__ */ a__default.createElement(
        CaretIcon,
        {
          color: disabled ? "onLight.disabled.muted" : "onLight.icon.secondary",
          size: "sm"
        }
      )
    ), isOpen && createPortal(
      /* @__PURE__ */ a__default.createElement(
        "ul",
        {
          ...dropdownProps,
          className: classNames(
            list,
            listHeight[hasCaption ? "withCaption" : "default"],
            listDisplay[isOpen ? "default" : "hidden"]
          )
        },
        /* @__PURE__ */ a__default.createElement(
          Empty,
          {
            emptyLabel,
            isLoading: !!isLoading,
            noOptionsProvided
          }
        ),
        /* @__PURE__ */ a__default.createElement(Loading, { isLoading: !!isLoading }),
        options == null ? void 0 : options.map((option, i) => /* @__PURE__ */ a__default.createElement(
          Option,
          {
            isHighlighted: i === activeIndex,
            isSelected: i === selectedIndex,
            itemProps: {
              "aria-selected": i === selectedIndex && i === activeIndex,
              role: "option",
              tabIndex: i === activeIndex ? 0 : -1,
              ref: (node) => {
                listRef.current[i] = node;
              },
              ...getItemProps({
                // Handle pointer select.
                onClick() {
                  handleSelect(i);
                },
                // Handle keyboard select.
                onKeyDown(event) {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSelect(i);
                  }
                  if (event.key === " " && !isTypingRef.current) {
                    event.preventDefault();
                    handleSelect(i);
                  }
                }
              })
            },
            key: option.value,
            option
          }
        ))
      ),
      document.body
    ))
  );
}
const Select = /* @__PURE__ */ forwardRef(SelectComponent);
export {
  Select
};
